import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Rating,
  Typography,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { CustomQuestionStats } from '../components/profile/custom-questions/CQTable';
import { QuestionSeenStats } from '../common/components/QuestionSeenStats';
import { average } from '../components/room/utils';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import { roundToOneDecimal } from '@par/shared/utils/math';

const REASONS = [
  {
    value: 'format',
    description: 'Format/incomplete (par, category, alts, etc)',
  },
  { value: 'confusing', description: `Confusing wording/couldn't understand` },
  { value: 'flow', description: `Poor flow/ordering of clues` },
  {
    value: 'not-notable',
    description: `Not notable (research before selecting)`,
  },
  {
    value: 'unintersting',
    description: `Uninteresting content (aka "I just don't like it")`,
  },
];

type PropTypes = {
  onClose: () => void;
  open: boolean;
  profileId: string;
  question?: CustomQuestionStats;
};

export const QuestionStatsDialog = ({
  onClose,
  open,
  profileId,
  question,
}: PropTypes) => {
  const theme = useTheme();
  const [disabled, setDisabled] = useState(false);
  const [authorComment, setAuthorComment] = useState<{
    questionId: string;
    authorComment: string;
    referenceLink: string;
  }>();

  useEffect(() => {
    if (!open) return;
    setDisabled(question.exclude);
    if (question.submittedBy) {
      fetchAuthorComment();
    }
  }, [open]);

  const getLowRatingReasonCount = (reason: string) => {
    const reasons = question?.stats?.stars?.filter(
      (s) => s.lowRatingReason === reason
    );
    return reasons.length;
  };

  const handleDisableQuestion = async () => {
    const res = await fetchPost('/custom-questions/disable-question', {
      profileId,
      questionId: question.questionId,
      disable: !question.exclude,
    });
    if (res) {
      setDisabled((prev) => !prev);
    }
  };

  const fetchAuthorComment = async () => {
    console.log('q', question);
    const res = await fetchGet(
      `/custom-questions/author-comment/${question.questionId}`
    );
    console.log(res);
    setAuthorComment(res);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <Typography
          variant="h5"
          textAlign={'center'}
          fontWeight={600}
          color="primary"
        >
          CUSTOM QUESTION STATS
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Stack spacing={2}>
          {disabled ? (
            <Stack
              spacing={2}
              alignItems={'baseline'}
              sx={{
                padding: '10px',
                border: '1px solid',
                borderColor: 'warning.main',
                borderRadius: '5px',
              }}
            >
              <Typography
                variant="subtitle1"
                textAlign={'center'}
                width="100%"
                sx={{ color: theme.palette.warning.main }}
              >
                This question has been disabled and will not appear in games
              </Typography>
            </Stack>
          ) : null}
          {question.rejectionComment ? (
            <Stack
              spacing={2}
              alignItems={'baseline'}
              sx={{
                padding: '10px',
                backgroundColor: '#1a2027',
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: '5px',
              }}
            >
              <Typography variant="subtitle1" sx={{ color: '#ff7878' }}>
                Rejection reason: {question.rejectionComment}
              </Typography>
            </Stack>
          ) : null}
          <Stack
            spacing={2}
            alignItems={'baseline'}
            sx={{
              padding: '10px',
              backgroundColor: '#1a2027',
              border: '1px solid #634343',
              borderRadius: '5px',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 4 }}
            >
              <Stack direction={'row'} spacing={1}>
                <Typography variant="subtitle1" color="primary">
                  Category:
                </Typography>
                <Typography variant="subtitle1">
                  {question?.category}
                </Typography>
              </Stack>
              <Stack direction={'row'} spacing={1}>
                <Typography variant="subtitle1" color="primary">
                  PAR:
                </Typography>
                <Typography variant="subtitle1">{question?.par}</Typography>
              </Stack>
            </Stack>
            <Stack
              direction={'row'}
              spacing={1}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography variant="subtitle1" color="primary">
                Question:
              </Typography>
              <Typography variant="subtitle1">{question?.question}</Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 4 }}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Stack direction={'row'} spacing={1}>
                <Typography variant="subtitle1" color="primary">
                  Answer:
                </Typography>
                <Typography variant="subtitle1">{question?.answer}</Typography>
              </Stack>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 4 }}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Stack direction={'row'} spacing={1}>
                <Typography variant="subtitle1" color="primary">
                  Alternate Answers:
                </Typography>
                <Typography variant="subtitle1">
                  {question?.alternateAnswers.length
                    ? question?.alternateAnswers.join(',')
                    : '--'}
                </Typography>
              </Stack>
              <Stack direction={'row'} spacing={1}>
                <Typography variant="subtitle1" color="primary">
                  Prompts:
                </Typography>
                <Typography variant="subtitle1">
                  {question?.promptAnswer.length
                    ? question?.promptAnswer.join(',')
                    : '--'}
                </Typography>
              </Stack>
            </Stack>
            {authorComment ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 4 }}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Stack direction={'row'} spacing={1}>
                  <Typography variant="subtitle1" color="primary">
                    Author's comment:
                  </Typography>
                  <Typography variant="subtitle1">
                    {authorComment.authorComment}
                  </Typography>
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <Typography variant="subtitle1" color="primary">
                    Reference link:
                  </Typography>
                  <Typography variant="subtitle1">
                    <Link target="_blank" href={authorComment.referenceLink}>
                      {authorComment.referenceLink}
                    </Link>
                  </Typography>
                </Stack>
              </Stack>
            ) : null}
          </Stack>
          <QuestionSeenStats
            par={question.par}
            questionStats={question.stats}
          />
          {question.stats?.stars && question.stats?.stars.length ? (
            <Stack
              spacing={2}
              alignItems={'baseline'}
              width="100%"
              sx={{
                padding: '10px',
                backgroundColor: '#1a2027',
                border: '1px solid #634343',
                borderRadius: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography textAlign={'center'}>RATINGS</Typography>
              <Stack direction="row" spacing={2} width={'100%'}>
                <Box width="40%">
                  <Stack
                    spacing={2}
                    sx={{
                      height: '80%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Rating
                      max={3}
                      precision={0.1}
                      value={average(question.stats.stars.map((s) => s.rating))}
                      readOnly
                    />
                    <Stack direction="row" spacing={2}>
                      <Typography>
                        <Typography
                          variant="h6"
                          display={'inline'}
                          fontWeight={600}
                        >
                          {question.stats.stars.length}
                        </Typography>{' '}
                        player{question.stats.stars.length !== 1 ? 's' : ''}{' '}
                        rated this
                      </Typography>
                      <Typography>
                        <Typography
                          variant="h6"
                          display={'inline'}
                          fontWeight={600}
                        >
                          {roundToOneDecimal(
                            average(question.stats.stars.map((s) => s.rating))
                          )}
                        </Typography>{' '}
                        / 3
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box width="60%">
                  <Stack
                    spacing={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography>Low Rating Reasons</Typography>
                    <Stack spacing={1}>
                      {REASONS.map((r) => (
                        <Stack direction={'row'} spacing={2}>
                          <Typography width="30px">
                            {getLowRatingReasonCount(r.value)}
                          </Typography>
                          <Typography>{r.description}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
        <Button
          variant="contained"
          sx={{
            backgroundColor: disabled
              ? theme.palette.underPar.main
              : theme.palette.demon.main,
            color: 'white',
            mt: 2,
          }}
          onClick={handleDisableQuestion}
        >
          {disabled ? 'ENABLE THIS QUESTION' : 'DISABLE THIS QUESTION'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
